import { getPlayerById } from '../state/selectors';
import { endTurn } from './turnActions';
import { drawCards } from '../cards/effectHelpers';
import { HAND_SIZE } from '../constants';

// Places `orderedInstanceIds` (a subset of hand, in draw order - the first id
// will be the very next card drawn) on top of the deck; whatever's left in hand
// is discarded as part of endTurn's normal cleanup. Pure deck manipulation, no
// gold/XP/buying (rulebook, "Prepare").
export function prepare(state, { playerId, orderedInstanceIds }) {
  const player = getPlayerById(state, playerId);
  for (const id of orderedInstanceIds) {
    if (!player.hand.includes(id)) throw new Error('Can only prepare cards that are in hand.');
  }
  const kept = new Set(orderedInstanceIds);
  const remainder = player.hand.filter((id) => !kept.has(id));

  const players = state.players.map((p) => (p.id === playerId
    ? {
      ...p, deck: [...orderedInstanceIds, ...p.deck], hand: remainder, stats: { ...p.stats, timesPrepared: p.stats.timesPrepared + 1 },
    }
    : p));

  return endTurn({ ...state, players });
}

// Numenera, Enhanced Rugged Jack: "React: After another player selects a
// monster with your mark to fight, take a prepare action." A self-contained
// variant of the mechanic above, deliberately NOT sharing endTurn - this
// fires mid another player's OWN turn (actions/dungeonActions.js's
// resolveMonsterMark, called from chooseMonster before combat even
// resolves), so it must not touch activePlayerIndex/turnNumber or reset
// turnFlags for a player who isn't currently active. Matt, scoping the
// fidelity: the CORE Prepare mechanic only (keep N cards on deck in order,
// discard the rest, draw a fresh hand) - deliberately skips every other
// end-of-turn-specific special case endTurn otherwise handles (Filigree
// Amulet's destroy-instead-of-discard, Elven Waybread's deferred
// placement, familiar cycling, hand-size modifiers) since those are all
// conceptually tied to "this turn is ending," which doesn't apply to a
// bonus action granted mid someone else's turn. Resolved via a queued
// choice (effectHelpers.js's queuePreparePick/actions/handChoiceActions.js's
// resolvePreparePick) since - like every other Battle/Aftermath/React-
// triggered decision - there's no way to pause mid-dispatch to ask which
// cards to keep.
export function applyBonusPrepare(state, playerId, orderedInstanceIds) {
  const player = getPlayerById(state, playerId);
  for (const id of orderedInstanceIds) {
    if (!player.hand.includes(id)) throw new Error('Can only prepare cards that are in hand.');
  }
  const kept = new Set(orderedInstanceIds);
  const remainder = player.hand.filter((id) => !kept.has(id));
  const rearranged = {
    ...state,
    players: state.players.map((p) => (p.id === playerId
      ? {
        ...p,
        deck: [...orderedInstanceIds, ...p.deck],
        hand: [],
        discard: [...p.discard, ...remainder],
        stats: { ...p.stats, timesPrepared: p.stats.timesPrepared + 1 },
      }
      : p)),
  };
  return drawCards(rearranged, playerId, HAND_SIZE);
}
